import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import Loading from "../common/Loading";
import ButtonType from "../common/button/ButtonType";
import ClientSSO from "../../model/ClientSSO";
import ClientSSOMetadata from "../../model/ClientSSOMetadata";

const defaultFormData = new ClientSSO(
  "",
  "",
  new ClientSSOMetadata("", "", "", "", "", "", "")
);

function ClientSSODetails() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<ClientSSO>(defaultFormData);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let clientSSO: ClientSSO | undefined = undefined;
        if (id) {
          clientSSO = await apiClient?.get<ClientSSO>({
            url: APIEndPoints.CLIENTS.ONE(parseInt(id)),
          });
          setData(clientSSO);
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="p-4 border border-solid border-gray-200 rounded-xl mx-52 my-5 text-gray-600">
      <div className="flex justify-between">
        <h2> {data.client_metadata.client_name}</h2>

        <Button
          label="Edit information"
          purpose={ButtonAction.EDIT}
          type={ButtonType.ICON_TEXT}
          onClick={() =>
            navigate(
              SETTINGS.get_path({
                section: AppSections.CLIENTS,
                id: data.id,
                subsection: AppSubSections.EDIT,
              })
            )
          }
        />
      </div>

      <table className="table-auto w-auto border-separate border-spacing-y-4">
        <tbody>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Id</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_id ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Secret</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_secret ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">URI</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.client_uri ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Redirect URI</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.redirect_uris ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Grant Types</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.grant_types ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Response Type</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.response_types ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">Scope</td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.scope ?? "Not found"}
            </td>
          </tr>
          <tr className="text-left">
            <td className="px-4 py-2 font-semibold">
              Token Endpoint Auth Method
            </td>
            <td className="border border-gray-300 px-4 py-2 hover:bg-blue-50">
              {data.client_metadata.token_endpoint_auth_method ?? "Not found"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default ClientSSODetails;
